/* eslint-disable @typescript-eslint/no-empty-object-type */
import type { Annotation } from "@/types/annotation";

export enum QuestionType {
  ADDRESS = "ADDRESS",
  DATE = "DATE",
  TEXT = "TEXT",
  NUMBER = "NUMBER",
  TEXT_AREA = "TEXT_AREA",
  MULTI_SELECT = "MULTI_SELECT",
  SINGLE_SELECT = "SINGLE_SELECT",
  RADIAL_SINGLE_SELECT = "RADIAL_SINGLE_SELECT",
  RADIAL_MULTI_SELECT = "RADIAL_MULTI_SELECT",
  TOGGLE = "TOGGLE",
  CHECKBOX = "CHECKBOX",
  SIGNATURE = "SIGNATURE",
  INITIALS = "INITIALS",
  PROVIDER_SIGNATURE = "PROVIDER_SIGNATURE",
  FILE_UPLOAD = "FILE_UPLOAD",
  PHONE_NUMBER = "PHONE_NUMBER",
  EMAIL = "EMAIL",
  DISCLAIMER = "DISCLAIMER",
  STATEMENT = "STATEMENT",
  DOCUMENT = "DOCUMENT",
  TAG = "TAG",
  PAYMENT_METHOD = "PAYMENT_METHOD",
  PHARMACY = "PHARMACY",
  LOCATION = "LOCATION",
  POUNDS_WEIGHT = "POUNDS_WEIGHT",
  FEET_INCHES_HEIGHT = "FEET_INCHES_HEIGHT",
  CALCULATED_SCORE = "CALCULATED_SCORE",
  MEDICATIONS = "MEDICATIONS",
  SOLD_ITEMS = "SOLD_ITEMS",
  SUPPLIES_USED = "SUPPLIES_USED",
  REBOOKING = "REBOOKING",
  SCRIBE_ATTACHMENTS = "SCRIBE_ATTACHMENTS",
  BODY_MAP = "BODY_MAP",
  DENTAL_CHART = "DENTAL_CHART",
  PERIO_CHART = "PERIO_CHART",
  OCCLUSION_EXAM = "OCCLUSION_EXAM",
  TABLE = "TABLE",
}

/**
 * Question types that accept free-text suggestion inserts. Used by the
 * scribe suggestions sidebar (to decide whether to surface suggestions for
 * the focused question) and by the suggestion-edit form (to filter scope
 * targets). When adding a new text-shaped QuestionType, add it here too.
 */
export const SUGGESTION_INSERTABLE_QUESTION_TYPES: ReadonlySet<QuestionType> =
  new Set([QuestionType.TEXT, QuestionType.TEXT_AREA]);

export interface QuestionData {
  name: string;
  description?: string;
  prefilledText?: string;
  /**
   * Label for the chart rows a health-mapped question writes. Set it and each
   * selected answer is stored as "<label> (<answer>)" — so a "Family History:
   * Stroke" question whose options are relatives produces "Stroke (Mother)"
   * rather than "Mother", which loses the condition. Undefined stores the answer
   * verbatim, which is every existing question.
   */
  chartEntryLabel?: string;
  /**
   * Lead forms only. The page link can answer the question with
   * `?<question id>=<value>`; when it does, the visitor doesn't see it.
   */
  hideWhenPrefilled?: boolean;
}

export interface Text extends QuestionData {
  placeholder: string;
  prefilledText?: string;
}

export interface Number extends QuestionData {
  placeholder: string;
}

export interface Address extends QuestionData {}

export interface DateOfBirth extends QuestionData {}

export type RelativeDate =
  | { years: number; months?: number; days?: number }
  | { years?: number; months: number; days?: number }
  | { years?: number; months?: number; days: number };

export interface Date extends QuestionData {
  minDate?: string;
  maxDate?: string;
  minTimeAgo?: RelativeDate;
  /**
   * Note templates only. When true the answer carries a time as well as a date and is
   * stored as a UTC instant instead of the date-only "YYYY-MM-DD", then converted to
   * the viewer's local zone for editing and display.
   */
  includeTime?: boolean;
  /** Note templates only. Shows a button that fills in the clinic's current date. */
  showTodayButton?: boolean;
}

export interface MultiSelect extends QuestionData {
  options: string[];
  optionLabels?: string[];
  /** Note templates only. Options already selected when a manual note is opened. */
  defaultOptions?: string[] | null;
  maxSelections?: number;
  minSelections?: number;
  /** What the dropdown shows before anything is picked; empty keeps "Select options...". */
  placeholder?: string | null;
}

export interface SingleSelect extends QuestionData {
  options: string[];
  optionLabels?: string[];
  /** Note templates only. At most one entry: the option a manual note opens with. */
  defaultOptions?: string[] | null;
  /** Picking the chosen option again clears it. */
  allowDeselect?: boolean | null;
  /** What a dropdown shows before anything is picked; empty keeps "Select an option". */
  placeholder?: string | null;
}

/**
 * Lead forms only: one of the clinic's in-person locations, which becomes the
 * patient's primary location. The server fills in the choices (location ids, with
 * their names as labels) whenever the form is read; they are never saved.
 */
export interface LocationChoice extends QuestionData {
  options?: string[];
  optionLabels?: string[];
  /** What the dropdown shows before a location is picked. */
  placeholder?: string | null;
}

export interface Toggle extends QuestionData {
  /** The switch gains an unanswered state: unanswered → Yes → No → unanswered. */
  allowDeselect?: boolean | null;
}

/** One box to tick. Ticked answers true; unticked is unanswered, never false. */
export type Checkbox = QuestionData;

export interface Tag extends QuestionData {
  placeholder: string;
  allowedTags?: string[];
}

export interface Signature extends QuestionData {
  text?: string;
}

export interface Initials extends QuestionData {
  /** The statement the patient is initialing. */
  text?: string;
}

export interface ProviderSignature extends QuestionData {
  text?: string;
}

export interface FileUpload extends QuestionData {
  allowedFileTypes: string;
  maxFileSize: number;
  maxFileCount: number;
}

export interface Authorization extends QuestionData {
  fileUrls?: string[];
  statements?: string[];
}

export interface PaymentMethod extends QuestionData {}

export interface Pharmacy extends QuestionData {}

export interface CalculatedScore extends QuestionData {
  displayFormat: string;
  calculationType: string;
}

export interface Disclaimer extends QuestionData {
  content: string;
}

export interface DocumentDetails {
  name: string;
  path: string;
}

export interface Document extends QuestionData {
  docs: DocumentDetails[];
}

export interface Statement extends QuestionData {
  points: string[];
}

export interface PlaceholderImage {
  id: string;
  name: string;
  gcsPath: string;
  signedUrl?: string; // Added by backend when returning data
  // Marks drawn on the template (a standard dosing pattern, say), copied onto
  // the note's attachment when the image is added.
  annotations?: Annotation[];
}

export interface ScribeAttachments extends QuestionData {
  placeholders: PlaceholderImage[];
  // When true, placeholder images aren't copied onto new notes; the provider
  // picks the ones they want from a gallery instead.
  pickFromGallery?: boolean;
}

export interface BodyMap extends QuestionData {
  imagePath: string;
}

// ============================================================================
// Dental question types — note templates only
// ============================================================================

export type ToothNumberingSystem = "UNIVERSAL" | "FDI" | "PALMER";
export type Dentition = "PERMANENT" | "PRIMARY" | "MIXED";
export type ToothSurface = "M" | "O" | "D" | "B" | "L" | "I";
export type ToothChartStatus =
  | "EXISTING"
  | "PLANNED"
  | "COMPLETED"
  | "REFERRED";
export type PerioAspect =
  | "MAXILLARY_BUCCAL"
  | "MAXILLARY_LINGUAL"
  | "MANDIBULAR_BUCCAL"
  | "MANDIBULAR_LINGUAL";
export type PerioSite = "DB" | "MB" | "MesB" | "DL" | "ML" | "MesL";
export type MobilityGrade = 0 | 1 | 2 | 3;
export type FurcationGrade = 0 | 1 | 2 | 3;
export type AngleClass = "I" | "II_DIV_1" | "II_DIV_2" | "III";

/** Whole-tooth conditions paintable in the odontogram. */
export type ToothConditionCode =
  | "MISSING"
  | "IMPLANT"
  | "IMPLANT_CROWN"
  | "BRIDGE_PONTIC"
  | "BRIDGE_ABUTMENT"
  | "CROWN"
  | "RCT"
  | "EXTRACTION_INDICATED"
  | "IMPACTED"
  | "UNERUPTED"
  | "SUPERNUMERARY"
  | "SEALANT_FULL"
  | "MOBILITY";

/** Per-surface findings paintable in the odontogram. FRACTURE and VENEER
 * are intrinsically surface-bound — a fracture happens on specific surfaces
 * (MB cusp, MOD crack, buccal craze line), and a veneer is by definition
 * applied to the facial/buccal surface only. */
export type SurfaceConditionCode =
  | "CARIES"
  | "COMPOSITE"
  | "AMALGAM"
  | "GOLD"
  | "CERAMIC"
  | "SEALANT"
  | "STAIN"
  | "WATCH"
  | "FRACTURE"
  | "VENEER";

/** DENTAL_CHART config (NoteQuestion.data). */
export interface DentalChart extends QuestionData {
  numberingSystem: ToothNumberingSystem;
  dentition: Dentition;
  enabledToothConditions?: ToothConditionCode[];
  enabledSurfaceConditions?: SurfaceConditionCode[];
}

export interface DentalChartToothEntry {
  condition?: ToothConditionCode;
  surfaces?: Partial<Record<ToothSurface, SurfaceConditionCode>>;
  status?: ToothChartStatus;
  note?: string;
}

/** DENTAL_CHART answer (NoteAnswer.data). */
export interface DentalChartAnswer {
  teeth: Record<string, DentalChartToothEntry>;
}

/** TABLE config (NoteQuestion.data). The template author fixes the row
 *  labels and column headers; the clinician fills a free-text value into
 *  each cell at note time. */
export interface Table extends QuestionData {
  rows: string[];
  columns: string[];
}

/** TABLE answer (NoteAnswer.data.value). Row labels and columns are
 *  snapshotted alongside the cells so a signed note stays self-contained
 *  even if the template's table is later edited. `cells` is row-major:
 *  cells[rowIndex][columnIndex]. */
export interface TableAnswer {
  rows: string[];
  columns: string[];
  cells: string[][];
  /** Optional per-column pixel widths from drag-resizing; empty = auto/equal. */
  widths?: number[];
}

/** PERIO_CHART config. The chart is comprehensive by design — every
 *  finding (PD, REC, BOP, plaque, suppuration, mobility, furcation) is
 *  always available. Recare visits just leave more cells empty. */
export interface PerioChart extends QuestionData {
  numberingSystem: ToothNumberingSystem;
  aspects: PerioAspect[];
}

export interface PerioSiteEntry {
  pd?: number;
  gm?: number;
  bop?: boolean;
  plaque?: boolean;
  sup?: boolean;
}

export interface PerioToothEntry {
  sites?: Partial<Record<PerioSite, PerioSiteEntry>>;
  mobility?: MobilityGrade;
  furcation?: Partial<Record<"B" | "L" | "M" | "D", FurcationGrade>>;
}

export interface PerioChartAnswer {
  teeth: Record<string, PerioToothEntry>;
}

/** OCCLUSION_EXAM config. */
/** OCCLUSION_EXAM config. All sections (Angle's, overjet/overbite,
 *  crossbite, openbite, midline, wear, TMJ) always render — clinicians
 *  fill what's relevant. */
export type OcclusionExam = QuestionData;

export interface AngleClassPair {
  right?: AngleClass;
  left?: AngleClass;
}

export interface CrossbiteFindings {
  anterior?: boolean;
  posteriorRight?: boolean;
  posteriorLeft?: boolean;
}

export interface OpenbiteFindings {
  anterior?: boolean;
  posterior?: boolean;
}

export interface MidlineShift {
  upperMm?: number;
  lowerMm?: number;
}

export interface WearFindings {
  generalized?: boolean;
  wornTeeth?: string[];
  localized?: string;
}

export interface TMJSideFindings {
  clicking?: boolean;
  popping?: boolean;
  crepitus?: boolean;
  pain?: boolean;
  deviation?: boolean;
}

export interface TMJFindings {
  right?: TMJSideFindings;
  left?: TMJSideFindings;
  maxOpeningMm?: number;
  notes?: string;
}

export interface OcclusionExamAnswer {
  angleClass?: AngleClassPair;
  overjetMm?: number;
  overbiteMm?: number;
  overbitePercent?: number;
  crossbite?: CrossbiteFindings;
  openbite?: OpenbiteFindings;
  midlineShift?: MidlineShift;
  wear?: WearFindings;
  tmj?: TMJFindings;
}

export interface QuestionCondition {
  id: string;
  questionId: string;
  dependentQuestionId: string;
  operator: string;
  value: string;
  groupId?: string;
}

export interface QuestionDetail<T extends QuestionData> {
  id: string;
  blockId: string;
  formId: string;
  position: number;
  questionType: QuestionType;
  required: boolean;
  isArchived: boolean;
  mappingKey?: string;
  measurementFieldId?: string;
  data: T;
  conditions?: QuestionCondition[];
}

export interface QuestionSummary {
  id: string;
  questionType: QuestionType;
  name: string;
  isArchived: boolean;
  options?: string[];
  optionLabels?: string[];
  /** INITIALS only: the statement the patient initialed, as they saw it. */
  text?: string;
}

export enum RebookingIntervalUnit {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
}

/** REBOOKING config (NoteQuestion.data). The interval here only seeds the
 *  picker; the provider changes it per selected service at note time. */
export interface Rebooking extends QuestionData {
  defaultIntervalValue: number;
  defaultIntervalUnit: RebookingIntervalUnit;
}

/** One entry of a REBOOKING answer (NoteAnswer.data.rebookings). */
export interface RebookingSelection {
  serviceId: string;
  intervalValue: number;
  intervalUnit: RebookingIntervalUnit;
  /** Minutes added to (positive) or taken off (negative) the service's default
   *  length, when the provider wants the follow-up booked longer or shorter.
   *  An adjustment, not a total, so it stays right if the clinic changes the
   *  service's length. Absent on older answers. */
  adjustMinutes?: number;
}

/** One supply the template author lists, tied to the stock it draws from. */
export interface SupplyOption {
  label: string;
  stockId: string;
}

/** SUPPLIES_USED config (NoteQuestion.data). */
export interface SuppliesUsed extends QuestionData {
  options: SupplyOption[];
}

/** One entry of NoteAnswer.data.supplies. Saving the note makes the
 *  NoteSupply records, which hold the stock, match it. */
export interface SupplySelection {
  stockId: string;
  label: string;
  quantity: number;
  shipmentId: string | null;
  lotNumber: string | null;
}

export interface SupplyLotOption {
  shipmentId: string;
  lotNumber: string | null;
  expiryDate: string | null;
  receivedDate: string;
  available: number;
}

export interface NoteSupply {
  stockId: string;
  shipmentId: string;
  name: string;
  lotNumber: string | null;
  quantity: number;
  /** What it holds in stock; below quantity when stock ran short. */
  drawn: number;
}

export interface NoteSuppliesState {
  supplies: NoteSupply[];
  lotsByStockId: Record<string, SupplyLotOption[]>;
  latestNoteId: string;
}
